import { CommonGamesMethodology } from './enums/nfl/common-games-methodology';
import * as userStoreClient from './user-store-client';

export interface ISchedulePickerConfig {
  DivToggle: boolean;
  HideStatus: boolean;
  StickyColumns: boolean;
}

export type NhlViewMode = 'conference' | 'division' | 'wildcard';

export interface INHLConfig extends ISchedulePickerConfig {
  ViewMode: NhlViewMode;
}

export interface INBAConfig extends ISchedulePickerConfig {}
export interface IMLBConfig extends ISchedulePickerConfig {}
export interface ISECConfig extends ISchedulePickerConfig {}
export interface IPac12Config extends ISchedulePickerConfig {}
export interface IBig10Config extends ISchedulePickerConfig {}
export interface IACCConfig extends ISchedulePickerConfig {}

export interface INFLConfig extends ISchedulePickerConfig {
  CommonGamesMethodology: CommonGamesMethodology;
  TreatNonPlayoffTeamsLikeWildcards: boolean;
  ShowSOV: boolean;
  StatsColumns: string[];
}

export interface IUserConfig {
  IsDebugMode: boolean;
  NHL: INHLConfig;
  NBA: INBAConfig;
  NFL: INFLConfig;
  MLB: IMLBConfig;
  SEC: ISECConfig;
  Pac12: IPac12Config;
  Big10: IBig10Config;
  ACC: IACCConfig;
}

const STORAGE_KEY = 'UserConfig';
const LEGACY_KEYS_MIGRATED_KEY = 'pp_legacy_keys_migrated';

class UserConfigManager {
  UserConfig: IUserConfig;

  constructor() {
    const raw = localStorage.getItem(STORAGE_KEY);
    this.UserConfig = raw ? JSON.parse(raw) : null;
    this.assertValid();
  }

  assertValid(): void {
    const defaultUserConfig: IUserConfig = {
      IsDebugMode: false,
      NHL: {
        ViewMode: 'division',
        DivToggle: true,
        HideStatus: false,
        StickyColumns: false,
      },
      MLB: {
        DivToggle: true,
        HideStatus: false,
        StickyColumns: false,
      },
      NBA: {
        DivToggle: true,
        HideStatus: false,
        StickyColumns: false,
      },
      NFL: {
        DivToggle: true,
        CommonGamesMethodology: CommonGamesMethodology.RequireGamesBePlayed,
        TreatNonPlayoffTeamsLikeWildcards: true,
        HideStatus: false,
        StickyColumns: false,
        ShowSOV: false,
        StatsColumns: [],
      },
      SEC: {
        DivToggle: true,
        HideStatus: false,
        StickyColumns: false,
      },
      Pac12: {
        DivToggle: true,
        HideStatus: false,
        StickyColumns: false,
      },
      Big10: {
        DivToggle: true,
        HideStatus: false,
        StickyColumns: false,
      },
      ACC: {
        DivToggle: true,
        HideStatus: false,
        StickyColumns: false,
      },
    };

    // First-time load: no stored config
    if (!this.UserConfig) {
      this.UserConfig = defaultUserConfig;
    }

    // Merge stored values onto the default (ensures new keys get defaults)
    this.UserConfig = this.mergeRecursive(defaultUserConfig, this.UserConfig);

    // One-shot migration: remove orphan localStorage keys that are now in UserConfig
    if (!localStorage.getItem(LEGACY_KEYS_MIGRATED_KEY)) {
      localStorage.removeItem('columns');
      localStorage.removeItem('isDebugMode');
      localStorage.setItem(LEGACY_KEYS_MIGRATED_KEY, '1');
    }

    this.saveLocalStorage();
  }

  saveStorage(): void {
    this.saveLocalStorage();
    userStoreClient.putConfig(this.UserConfig).catch((err: unknown) => {
      console.error('[UserConfigManager] putConfig failed', err);
    });
  }

  private saveLocalStorage(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.UserConfig));
  }

  mergeRecursive<T>(obj1: T, obj2: T): T {
    for (const p in obj2) {
      try {
        if ((obj2[p] as any)?.constructor === Object) {
          obj1[p] = this.mergeRecursive(obj1[p], obj2[p]);
        } else {
          obj1[p] = obj2[p];
        }
      } catch {
        obj1[p] = obj2[p];
      }
    }
    return obj1;
  }

  /**
   * Fetches the user's config from AuthEngine and merges it into the local config.
   * Server blob wins for any key present in the remote config.
   * Called on app boot when isAuthenticated.
   */
  async hydrateFromServer(): Promise<void> {
    const remote = await userStoreClient.getConfig();
    if (remote !== null) {
      this.UserConfig = this.mergeRecursive(this.UserConfig, remote as IUserConfig);
      this.saveLocalStorage();
    }
  }
}

export const userConfigManager = new UserConfigManager();
